/**
 * WEB instance of the CourseModuleRegistry (F2-A): registers the v1 plugins
 * (lesson, resource) with their LAZY Solid components and the authoring's
 * "add module" menu UI metadata. F3 adds kinds (assignment/forum/video/
 * live_session) by registering its plugin here — without touching the
 * authoring/player.
 */
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import type { Component } from "solid-js";
import {
  CourseModuleRegistry,
  createAssignmentModulePlugin,
  createForumModulePlugin,
  createLessonModulePlugin,
  createLiveSessionModulePlugin,
  createResourceModulePlugin,
  // MEDIA-F3F: module kind `video`
  createVideoModulePlugin,
} from "@space/shared";

/** Common props for module components (authoring and player). */
export interface ModuleComponentProps {
  courseId: string;
  moduleId: string;
  refId?: string | null;
}

export type ModuleComponent = Component<ModuleComponentProps>;

export const moduleRegistry = new CourseModuleRegistry<ModuleComponent>();

moduleRegistry.registerAll([
  createLessonModulePlugin<ModuleComponent>({
    // The lesson is authored/played with the existing flow (blocks); these
    // loaders exist for generic registry consumers.
    playerComponent: () =>
      import("./resource-module").then((m) => m.ModuleFallback as ModuleComponent),
  }),
  createResourceModulePlugin<ModuleComponent>({
    authoringComponent: () =>
      import("./resource-module").then((m) => m.ResourceModuleEditor as ModuleComponent),
    playerComponent: () =>
      import("./resource-module").then((m) => m.ResourceModuleViewer as ModuleComponent),
  }),
  // MEDIA-F3F: module kind `video` (standalone player + pick/upload authoring).
  createVideoModulePlugin<ModuleComponent>({
    authoringComponent: () =>
      import("@/features/media/components/video-module").then(
        (m) => m.VideoModuleEditor as ModuleComponent,
      ),
    playerComponent: () =>
      import("@/features/media/components/video-module").then(
        (m) => m.VideoModuleViewer as ModuleComponent,
      ),
  }),
  // F3-A: live class (thin). The player shows the linked session's info;
  // the video call access screen is added by F3-G.
  createLiveSessionModulePlugin<ModuleComponent>({
    playerComponent: () =>
      import("./live-session-module").then((m) => m.LiveSessionModuleViewer as ModuleComponent),
  }),
  // F3-B: gradable assignment (authoring with rubric editor; player with submission).
  createAssignmentModulePlugin<ModuleComponent>({
    authoringComponent: () =>
      import("./assignment-module").then((m) => m.AssignmentModuleEditor as ModuleComponent),
    playerComponent: () =>
      import("./assignment-module").then((m) => m.AssignmentModulePlayer as ModuleComponent),
  }),
  // F3-C: module kind `forum` (thread list + thread view + moderation).
  createForumModulePlugin<ModuleComponent>({
    authoringComponent: () =>
      import("./forum-module").then((m) => m.ForumModuleEditor as ModuleComponent),
    playerComponent: () =>
      import("./forum-module").then((m) => m.ForumModuleViewer as ModuleComponent),
  }),
]);

/** Per-kind UI metadata ("add module" menu label). */
export interface ModuleKindMeta {
  kind: string;
  label: MessageDescriptor;
  hint: MessageDescriptor;
  /** Stable testid for the add button (e2e compat: lesson = "add-lesson"). */
  testId: string;
}

export const MODULE_KIND_META: ModuleKindMeta[] = [
  {
    kind: "lesson",
    label: msg`Lesson`,
    hint: msg`Interactive lesson with learning blocks`,
    testId: "add-lesson",
  },
  {
    kind: "resource",
    label: msg`Resource`,
    hint: msg`File, link or text material`,
    testId: "add-module-resource",
  },
  // MEDIA-F3F: module kind `video`
  {
    kind: "video",
    label: msg`Video`,
    hint: msg`On-demand video with resume`,
    testId: "add-module-video",
  },
  {
    kind: "live_session",
    label: msg`Live class`,
    hint: msg`Live session linked to a scheduled class`,
    testId: "add-module-live_session",
  },
  {
    kind: "assignment",
    label: msg`Assignment`,
    hint: msg`Gradable task with optional rubric and submission`,
    testId: "add-module-assignment",
  },
  // F3-C: module kind `forum`
  {
    kind: "forum",
    label: msg`Forum`,
    hint: msg`Cohort discussion: threads and replies`,
    testId: "add-module-forum",
  },
];

export function moduleKindMeta(kind: string): ModuleKindMeta | undefined {
  return MODULE_KIND_META.find((m) => m.kind === kind);
}
